import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "@lynx/ui-components";

import { accountThunks } from "actions-thunks/account-thunks";
import { setWireImageAC } from "actions-thunks/actions";
import { supportTableUtils } from "ui/ui-kit/react-table/supportTableUtils";

import "./wire-details-modal.scss";

const WireDetailsModal = ({ wire, onClose }) => {
  const dispatch = useDispatch();
  const { currentAccount, wireImage } = useSelector((state) => state.accounts);
  const { loading } = useSelector((state) => state.utils);
  const [imageLoading, setImageLoading] = useState(false);

  const isDeposit = true; // TODO: adjust based on wire type
  const displayType = isDeposit ? "Deposit" : "Pay-out";

  // Map backend status to display format
  const backendStatus = wire?.status || "";
  let statusText = backendStatus.toUpperCase();
  let statusIconPath;
  let statusClass;

  if (statusText.includes("PROGRESS") || statusText === "PENDING" || statusText === "PROCESSING") {
    statusText = "IN PROGRESS";
    statusIconPath = `${process.env.PUBLIC_URL}/icons/inprogress.svg`;
    statusClass = "status-in-progress";
  } else if (statusText.includes("CONFIRM") || statusText === "COMPLETED" || statusText === "RECEIVED" || statusText === "SENT") {
    statusText = "CONFIRMED";
    statusIconPath = `${process.env.PUBLIC_URL}/icons/done.svg`;
    statusClass = "status-confirmed";
  } else if (statusText.includes("DECLIN") || statusText === "REJECTED" || statusText === "FAILED") {
    statusText = "DECLINED";
    statusIconPath = `${process.env.PUBLIC_URL}/icons/decline.svg`;
    statusClass = "status-declined";
  } else {
    statusText = "IN PROGRESS";
    statusIconPath = `${process.env.PUBLIC_URL}/icons/inprogress.svg`;
    statusClass = "status-in-progress";
  }

  // Format amounts
  const formatAmount = (amount) => {
    if (typeof amount === 'number') {
      return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    }
    return amount || "$0.00";
  };

  const declaredAmount = wire?.amount?.pending || wire?.amount?.claimed || 0;
  const receivedAmount = wire?.amount?.received || 0;
  const hasConfirmation = wire?.claim?.confirmation && Object.keys(wire.claim.confirmation).length > 0;

  // Fetch wire image if confirmation exists
  useEffect(() => {
    if (hasConfirmation && wire?._id && currentAccount?._id) {
      setImageLoading(true);
      dispatch(accountThunks.getWireConfirmationImageTC(currentAccount._id, wire._id))
        .finally(() => setImageLoading(false));
    }
    return () => {
      // Clean up wire image when modal closes
      dispatch(setWireImageAC(null));
    };
  }, [hasConfirmation, wire?._id, currentAccount?._id, dispatch]);

  const isPDF = wireImage?.image?.split("/")[1]?.split(";")[0] === "pdf";

  const handleDownloadImage = () => {
    if (!wireImage?.image) return;

    const link = document.createElement("a");
    link.href = wireImage.image;
    
    if (isPDF) {
      link.download = `wire-confirmation-${wire._id}.pdf`;
    } else {
      link.download = `wire-confirmation-${wire._id}.jpg`;
    }
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClose = () => {
    dispatch(setWireImageAC(null));
    onClose();
  };

  const createdDate = wire?.requestedOn || wire?.createdAt || wire?.created || wire?.date;
  const updatedDate = wire?.received?.date || wire?.claim?.date || wire?.updatedAt || wire?.updated;

  return (
    <div className="modal-window">
      <Modal
        className="wire-details-modal cabinet-modals"
        styleType="zimtra-primary"
        isOpen={true}
        hideBlur
        hasCloseIcon={true}
        onClickOutside={handleClose}
      >
        <Modal.Header
          className="modal-inner-header"
          onClose={handleClose}
          title={<h2 className="zimtra-h2">Transaction Details</h2>}
        />
        <Modal.Body className="modal-inner-body">
          <div className="wire-details-content">
            <div className="detail-row">
              <div className="detail-label">Type:</div>
              <div className="detail-value">{displayType}</div>
            </div>

            {isDeposit ? (
              <>
                <div className="detail-row">
                  <div className="detail-label">Amount (declared):</div>
                  <div className="detail-value">{formatAmount(declaredAmount)}</div>
                </div>
                {receivedAmount > 0 && (
                  <div className="detail-row">
                    <div className="detail-label">Amount (received):</div>
                    <div className="detail-value">{formatAmount(receivedAmount)}</div>
                  </div>
                )}
              </>
            ) : (
              <div className="detail-row">
                <div className="detail-label">Amount:</div>
                <div className="detail-value">{formatAmount(declaredAmount)}</div>
              </div>
            )}

            <div className="detail-row">
              <div className="detail-label">Status:</div>
              <div className={`detail-value status-cell-with-icon ${statusClass}`}>
                {statusIconPath && (
                  <img
                    src={statusIconPath}
                    alt={statusText}
                    className="status-icon"
                  />
                )}
                {statusText}
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Created:</div>
              <div className="detail-value">
                {createdDate ? supportTableUtils.tableTimeFormat(createdDate) : "N/A"}
              </div>
            </div>

            <div className="detail-row">
              <div className="detail-label">Updated:</div>
              <div className="detail-value">
                {updatedDate ? supportTableUtils.tableTimeFormat(updatedDate) : "N/A"}
              </div>
            </div>

            {hasConfirmation && (
              <div className="detail-row attachment-row">
                <div className="detail-label">Attachment:</div>
                <div className="detail-value">
                  {imageLoading || loading ? (
                    <span>Loading...</span>
                  ) : wireImage?.image ? (
                    <button
                      type="button"
                      className="download-attachment-button"
                      onClick={handleDownloadImage}
                    >
                      {isPDF
                        ? `wire-confirmation-${wire._id}.pdf`
                        : `wire-confirmation-${wire._id}.jpg`}
                    </button>
                  ) : (
                    <span>Attachment available</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default WireDetailsModal;
